import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, FileText, User, Building2, Upload, Trash2, Paperclip } from 'lucide-react';
import { Activity, ActivityAttachmentUpload } from '../types';
import { contactsService, usersService, leadsService } from '../services/supabaseService';
import { activityAttachmentsService } from '../services/activityAttachmentsService';

interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<Activity>) => void;
  activity?: Activity | null;
}

const ActivityModal: React.FC<ActivityModalProps> = ({ isOpen, onClose, onSave, activity }) => {
  const [formData, setFormData] = useState({
    type: 'reunion' as Activity['type'],
    datetime: '',
    description: '',
    contactId: '',
    leadId: '',
    contactType: 'contact' as 'contact' | 'lead',
    owner: ''
  });

  const [availableContacts, setAvailableContacts] = useState<any[]>([]);
  const [availableLeads, setAvailableLeads] = useState<any[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attachments, setAttachments] = useState<ActivityAttachmentUpload[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadDropdownData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (activity) {
      setFormData({
        type: activity.type,
        datetime: activity.datetime.toISOString().slice(0, 16),
        description: activity.description,
        contactId: activity.contactId || '',
        leadId: activity.leadId || '',
        contactType: activity.leadId ? 'lead' : 'contact',
        owner: activity.owner
      });
    } else {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      setFormData({
        type: 'reunion',
        datetime: now.toISOString().slice(0, 16),
        description: '',
        contactId: '',
        leadId: '',
        contactType: 'contact',
        owner: ''
      });
    }
    setAttachments([]);
  }, [activity]);

  const loadDropdownData = async () => {
    try {
      setLoadingData(true);
      console.log('🔄 Cargando contactos, leads y usuarios para actividad...');

      const [contacts, leads, users] = await Promise.all([
        contactsService.getAll(),
        leadsService.getAll(),
        usersService.getAll()
      ]);

      const activeUsers = users.filter(user => user.isActive);
      const activeLeads = leads.filter(lead => lead.status !== 'convertido' && lead.status !== 'descartado');

      console.log('✅ Contactos cargados:', contacts.length);
      console.log('✅ Leads cargados:', activeLeads.length);
      console.log('✅ Usuarios activos cargados:', activeUsers.length);

      setAvailableContacts(contacts);
      setAvailableLeads(activeLeads);
      setAvailableUsers(activeUsers);

      if (!activity && activeUsers.length > 0) {
        setFormData(prev => ({
          ...prev,
          owner: activeUsers[0].name
        }));
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      setAvailableContacts([]);
      setAvailableLeads([]);
      setAvailableUsers([]);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const newErrors: Record<string, string> = {};
    
    if (!formData.type) {
      newErrors.type = 'El tipo de actividad es obligatorio';
    }
    
    if (!formData.datetime) {
      newErrors.datetime = 'La fecha y hora son obligatorias';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es obligatoria';
    }
    
    if (formData.contactType === 'contact' && !formData.contactId) {
      newErrors.contactId = 'El contacto es obligatorio';
    }

    if (formData.contactType === 'lead' && !formData.leadId) {
      newErrors.leadId = 'El lead es obligatorio';
    }
    
    if (!formData.owner.trim()) {
      newErrors.owner = 'El responsable es obligatorio';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      alert('Por favor, completa todos los campos obligatorios.');
      return;
    }
    
    const activityData: any = {
      type: formData.type,
      datetime: new Date(formData.datetime),
      description: formData.description,
      owner: formData.owner
    };

    if (formData.contactType === 'contact') {
      activityData.contactId = formData.contactId;
    } else {
      activityData.leadId = formData.leadId;
    }

    activityData.attachments = attachments.map(a => a.file);

    onSave(activityData);
  };

  const getActivityTypeIcon = (type: Activity['type']) => {
    switch (type) {
      case 'reunion': return Calendar;
      case 'llamada': return Clock;
      case 'correo': return FileText;
      case 'nota_interna': return FileText;
      default: return Clock;
    }
  };

  const getActivityTypeLabel = (type: Activity['type']) => {
    switch (type) {
      case 'reunion': return 'Reunión';
      case 'llamada': return 'Llamada';
      case 'correo': return 'Correo';
      case 'nota_interna': return 'Nota Interna';
      default: return 'Actividad';
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments: ActivityAttachmentUpload[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validation = activityAttachmentsService.validateFile(file);

      if (!validation.valid) {
        alert(`${file.name}: ${validation.error}`);
        continue;
      }

      if (attachments.length + newAttachments.length >= 5) {
        alert('Máximo 5 archivos por actividad');
        break;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const preview = activityAttachmentsService.isImageFile(file.type)
          ? event.target?.result as string
          : undefined;

        newAttachments.push({
          file,
          preview,
          progress: 0
        });

        if (newAttachments.length === Math.min(files.length - i, 5 - attachments.length)) {
          setAttachments(prev => [...prev, ...newAttachments]);
        }
      };

      if (activityAttachmentsService.isImageFile(file.type)) {
        reader.readAsDataURL(file);
      } else {
        newAttachments.push({
          file,
          progress: 0
        });
      }
    }

    if (!activityAttachmentsService.isImageFile(files[0]?.type)) {
      setAttachments(prev => [...prev, ...newAttachments]);
    }

    e.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleContactTypeChange = (type: 'contact' | 'lead') => {
    setFormData({
      ...formData,
      contactType: type,
      contactId: '',
      leadId: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {activity ? 'Editar Actividad' : 'Nueva Actividad'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Activity Type */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <Clock className="w-5 h-5" />
              <span>Tipo de Actividad</span>
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: 'reunion', label: 'Reunión', icon: Calendar },
                { value: 'llamada', label: 'Llamada', icon: Clock },
                { value: 'correo', label: 'Correo', icon: FileText },
                { value: 'nota_interna', label: 'Nota Interna', icon: FileText }
              ].map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: type.value as Activity['type'] })}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      formData.type === type.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <Icon className="w-5 h-5 mx-auto mb-2" />
                    <span className="text-sm font-medium">{type.label}</span>
                  </button>
                );
              })}
            </div>
            {errors.type && (
              <p className="text-red-600 text-xs mt-1">{errors.type}</p>
            )}
          </div>

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Información de la Actividad</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha y hora *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.datetime}
                  onChange={(e) => setFormData({ ...formData, datetime: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.datetime ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.datetime && (
                  <p className="text-red-600 text-xs mt-1">{errors.datetime}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asociar con *
                </label>

                <div className="flex space-x-4 mb-3">
                  <button
                    type="button"
                    onClick={() => handleContactTypeChange('contact')}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                      formData.contactType === 'contact'
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <User className="w-4 h-4 inline mr-2" />
                    Cliente
                  </button>
                  <button
                    type="button"
                    onClick={() => handleContactTypeChange('lead')}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                      formData.contactType === 'lead'
                        ? 'border-green-500 bg-green-50 text-green-700 font-medium'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <Building2 className="w-4 h-4 inline mr-2" />
                    Lead
                  </button>
                </div>

                {loadingData ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-gray-500">Cargando...</span>
                  </div>
                ) : formData.contactType === 'contact' ? (
                  <>
                    <select
                      value={formData.contactId}
                      onChange={(e) => setFormData({ ...formData, contactId: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.contactId ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Seleccionar cliente</option>
                      {availableContacts.map(contact => (
                        <option key={contact.id} value={contact.id}>
                          {contact.fullName} - {contact.company}
                        </option>
                      ))}
                    </select>
                    {errors.contactId && (
                      <p className="text-red-600 text-xs mt-1">{errors.contactId}</p>
                    )}
                  </>
                ) : (
                  <>
                    <select
                      value={formData.leadId}
                      onChange={(e) => setFormData({ ...formData, leadId: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                        errors.leadId ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Seleccionar lead</option>
                      {availableLeads.map(lead => (
                        <option key={lead.id} value={lead.id}>
                          {lead.fullName} - {lead.company} ({lead.status})
                        </option>
                      ))}
                    </select>
                    {errors.leadId && (
                      <p className="text-red-600 text-xs mt-1">{errors.leadId}</p>
                    )}
                  </>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsable *
                </label>
                {loadingData ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-gray-500">Cargando usuarios...</span>
                  </div>
                ) : (
                  <select
                    value={formData.owner}
                    onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.owner ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">Seleccionar responsable</option>
                    {availableUsers.map(user => (
                      <option key={user.id} value={user.name}>
                        {user.name} ({user.role === 'superadmin' ? 'Admin' : 'Usuario'})
                      </option>
                    ))}
                  </select>
                )}
                {errors.owner && (
                  <p className="text-red-600 text-xs mt-1">{errors.owner}</p>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción de la actividad *
            </label>
            <textarea
              rows={4}
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Describe detalladamente la actividad realizada..."
            />
            {errors.description && (
              <p className="text-red-600 text-xs mt-1">{errors.description}</p>
            )}
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Archivos Adjuntos (Opcional)
            </label>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                id="file-upload"
                multiple
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                onChange={handleFileSelect}
                className="hidden"
                disabled={attachments.length >= 5}
              />
              <label
                htmlFor="file-upload"
                className={`cursor-pointer ${attachments.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium text-blue-600">Click para subir</span> o arrastra archivos aquí
                </p>
                <p className="text-xs text-gray-500">
                  Máximo 5 archivos, 10MB cada uno
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Formatos: JPG, PNG, PDF, DOC, XLS, TXT, CSV
                </p>
              </label>
            </div>

            {attachments.length > 0 && (
              <div className="mt-4 space-y-2">
                {attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      {attachment.preview ? (
                        <img
                          src={attachment.preview}
                          alt={attachment.file.name}
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                          <Paperclip className="w-5 h-5 text-blue-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {attachment.file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {activityAttachmentsService.formatFileSize(attachment.file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(index)}
                      className="ml-3 text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Information Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">Información importante</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Las actividades se registran en el historial completo de todas las interacciones.
                  Puedes adjuntar documentos, imágenes o archivos relacionados con esta actividad.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {activity ? 'Actualizar' : 'Crear'} Actividad
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivityModal;